/* eslint-disable jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */
import { CartProvider, useCartContext } from '@/cart/context';
import { CmsProvider, useCmsContext } from '@/context/cms';
import {
  goHome,
  goToCheckout,
  goToMyAccountSavedCarts,
} from '@/utils/Common/Redirects';
import GigyaLoginPopupController from '@/utils/Controllers/Gigya/GigyaLoginPopupController';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import api
import isAuth from '@/api/get/isAuth';
import { notifyError } from '@/utils/Common/Notify';

// import hooks
import useFetchCart from '@/cart/hooks/useFetchCart';
import useFetchCmsComponents from '@/hooks/useFetchCmsComponents';
import useFirstRenderReveal from '@/hooks/useFirstRenderReveal';
import useGtmCheckout from '@/hooks/useGtmCheckout';
import { pushGtmCartSaveCart } from '@/utils/Controllers/GTM/Operations/Cart';

// import components
import AddAccessoriesPopUpLoader from '@/components/AddAccessoriesPopUpLoader';
import Button from '@/components/atoms/Button';
import ButtonSocial from '@/components/atoms/ButtonSocial';
import CartPromoBadge from '@/components/molecules/CartPromoBadge';
import BundleCartItem from '@/components/organisms/BundleCartItem';
import CartItem from '@/components/organisms/CartItem';
import OrderSummary from '@/components/organisms/OrderSummary';
import SubscriptionCartItem from '@/components/organisms/SubscriptionCartItem';
import SaveCartPopUpLoader from '@/components/SaveCartPopUpLoader';
import SmartEditSkeleton from '@/components/smartedit/SmartEditSkeleton';
import useTransformCartEntries from '@/hooks/useTransformCartEntries';
import CONST from '@/utils/Constants/General';

const TERMS_AND_CONDITIONS_LINK = {
  IT: '/it-it/condizioni-di-vendita-abbonamenti',
  EN: '/en-us/subscription-terms-and-conditions',
  ES: '/es-es/terminos-y-condiciones-de-suscripcion',
  DE: '/de-de/allgemeine-geschaftsbedingungen-abonnement',
  AT: '/de-at/allgemeine-geschaftsbedingungen-abonnement',
  CZ: '/cs-cz/vseobecne-obchodni-podminky-predplatneho',
  BE: '/fr-be/conditions-generales-abonnements',
  PT: '/pt-pt/termos-e-condicoes-de-venda-subscricoes',
  NL: '/nl-nl/algemene-verkoopvoorwaarden-abonnementen',
  PL: '/pl-pl/regulamin-subskrypcji',
};
let counter = false;

const CartApp = () => {
  const { t } = useTranslation();
  const [{ cart, cartLoading }] = useCartContext();
  const [{ components, componentsLoading }] = useCmsContext();
  const fetchLoading = componentsLoading || cartLoading;
  const isDesktop = () => window.matchMedia('(min-width:768px)').matches;

  const {
    hasEntries,
    totalEntries,
    standardEntries,
    bundles,
    hasSubscriptions,
    subscriptions,
  } = useTransformCartEntries(cart);

  const [
    isTermsAndConditionsAccepted,
    setIsTermsAndConditionsAccepted,
  ] = useState(false);
  const [
    showTermsAndConditionsMessage,
    setShowTermsAndConditionsMessage,
  ] = useState(false);
  const termsAndConditionsRef = useRef();

  useEffect(() => {
    const entry = document.getElementById('js-r-entry-cart');
    const { amountExceeded } = entry.dataset;
    const { quantityExceeded } = entry.dataset;
    const { lowStock } = entry.dataset;

    if (amountExceeded) {
      notifyError(t('cart:basket.validation.amountExceeded'));
    }
    if (quantityExceeded) {
      notifyError(t('cart:basket.validation.quantityExceeded'));
    }
    if (lowStock) {
      notifyError(t('cart:cart.validation.lowStock'));
    }
  }, []);

  useFetchCmsComponents('dlgCartPage');
  useFetchCart();
  useGtmCheckout({
    entries: cart && cart.entries,
    fetchLoading,
  });

  const termsAndConditionsLink = TERMS_AND_CONDITIONS_LINK[DLG.gigya.country];
  const termsAndConditionsLinkHTML = t('cart:cart.subscription.terms', {
    0: termsAndConditionsLink,
  });
  const termsAndConditionsError = t('cart:cart.subscription.mandatory.field');

  const wrapperRef = useFirstRenderReveal(fetchLoading);

  const amazonPayEnabled = DLG.config.isAmazonPayEnabled;

  if (fetchLoading) return null;

  const apAuthFailedEl = document.getElementById('amazonPayAuthFailed');

  if (apAuthFailedEl && counter === false) {
    const apAuthFailedData = apAuthFailedEl.dataset.isfailed;
    counter = true;

    if (apAuthFailedData === 'true') {
      notifyError(t('cart:cart.page.amazonpay.auth.failed'));
    }
  }

  const saveCarts = async () => {
    const isLogged = await isAuth();
    const successCB = () => {
      SaveCartPopUpLoader.load({ t });
    };
    if (isLogged) {
      successCB();
    } else {
      GigyaLoginPopupController.init({
        successCB,
        header: t('cart:cart.page.login.popup.header'),
        subHeader: t('cart:cart.page.login.popup.subheader'),
      });
    }
    if (DLG.config.brand === CONST.BRAND.DELONGHI) {
      pushGtmCartSaveCart();
    }
  };

  const handleAccessories = entry => {
    AddAccessoriesPopUpLoader.load({ entry });
  };

  const hasKWConfigurator =
    cart != null &&
    cart.entries != null &&
    cart.entries.some(e => e.kenwoodConfiguratorProduct);

  const termsAndConditionsAcceptanceHandler = () => {
    if (isTermsAndConditionsAccepted) {
      setShowTermsAndConditionsMessage(true);
    } else {
      setShowTermsAndConditionsMessage(false);
    }

    // Toggle Terms And Conditions state value
    setIsTermsAndConditionsAccepted(prevState => !prevState);
  };

  const goToCheckoutHandler = () => {
    if (hasSubscriptions && !isTermsAndConditionsAccepted) {
      setShowTermsAndConditionsMessage(true);

      // Trigger scroll
      const y =
        termsAndConditionsRef.current.getBoundingClientRect().top +
        window.scrollY;
      window.scroll({
        top: y - 150,
        behavior: 'smooth',
      });
    } else {
      goToCheckout();
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ opacity: 0 }}
      className={`dln-cart dln-page ${hasEntries ? '' : 'dln-cart--empty'}`}
    >
      <div className='dln-wrapper'>
        <div className='dln-cart__head'>
          {hasEntries && (
            <SmartEditSkeleton data={components.DLGChipsAdvantage} />
          )}
          <div className='dln-cart__titleWrapper'>
            <h1 className='dln-cart__title'>
              <i className='dln-icn dln-icn--iconfont'>n</i>
              <span className='dln-cart--title'>
                {t('cart:cart.page.title')}
              </span>
              <span className='dln-cart__titleLbl'>
                {t(
                  totalEntries === 1
                    ? 'cart:cart.page.total.item'
                    : 'cart:cart.page.total.items',
                  {
                    totalItem: totalEntries,
                  },
                )}
              </span>
            </h1>
            {hasEntries && !hasKWConfigurator && (
              <ButtonSocial
                onClick={saveCarts}
                text={t('cart:cart.save.cart.label')}
                modifiers={['link', 'icon']}
                glyph='c'
              />
            )}
          </div>
        </div>
        <div className='dln-cart__content'>
          {!hasEntries && (
            <div className='dln-cart__emptyMessage'>
              <i className='dln-icn dln-icn--iconfont'>n</i>
              <p>{t('cart:cart.page.empty')}</p>
              <div className='dln-cart__btnGroup'>
                <Button
                  onClick={goHome}
                  text={t('cart:cart.continue.shopping.label')}
                />
                <Button
                  onClick={goToMyAccountSavedCarts}
                  text={t('cart:cart.see.saved.cart.label')}
                  modifiers={['stroked']}
                />
              </div>
            </div>
          )}
          <div className='dln-cart__items'>
            {/* rendering bundles entries */}
            {bundles.map(bundle => (
              <BundleCartItem key={bundle.id} bundle={bundle} />
            ))}
            {/* rendering standard entries */}
            {standardEntries.map(entry => (
              <CartItem
                key={`${entry.entryNumber}-${entry.productCode}`}
                item={entry}
                addAccessories={handleAccessories}
              />
            ))}
            {/* rendering subscriptions entries */}
            {subscriptions.map(entry => (
              <SubscriptionCartItem
                key={`${entry.entryNumber}-${entry.productCode}`}
                item={entry}
                addAccessories={handleAccessories}
              />
            ))}
            {hasEntries && (
              <SmartEditSkeleton data={components.DLGChipsWarning} />
            )}
          </div>
          {isDesktop() && (
            <div className='js-sfmc-personalization-desktop-cart' />
          )}
          <CartPromoBadge
            title={t('cart:cart.promoBadge.title')}
            text1={cart.cartCouponPromotionMessage}
            text2={cart.cartDiscountPromotionMessage}
          />
          {hasSubscriptions && (
            <div
              ref={termsAndConditionsRef}
              className='dln-cart__bundle-terms-conditions'
            >
              <label
                className='dln-cart__bundle-terms-conditions-label'
                htmlFor='bundle-terms-conditions'
              >
                <input
                  className=''
                  type='checkbox'
                  checked={isTermsAndConditionsAccepted}
                  onChange={termsAndConditionsAcceptanceHandler}
                  id='bundle-terms-conditions'
                />
                <div
                  dangerouslySetInnerHTML={{
                    __html: termsAndConditionsLinkHTML,
                  }}
                />
              </label>
              {showTermsAndConditionsMessage && (
                <p className='dln-cart__bundle-terms-conditions--error'>
                  {termsAndConditionsError}
                </p>
              )}
            </div>
          )}
          {hasEntries && (
            <ButtonSocial
              onClick={goHome}
              modifiers={['link', 'icon']}
              glyph='s'
              text={t('cart:cart.continue.shopping')}
            />
          )}
        </div>
        <div className='dln-cart__aside'>
          {hasEntries && (
            <div className='dln-cart__orderRecap'>
              <OrderSummary
                order={cart}
                cartItem
                simplify
                modifiers={['light', 'cart']}
              />
              {hasSubscriptions
                ? components.DLGSubscriptionPaymentMethodBox && (
                    <div className='dln-cart__payment-wrapper'>
                      <SmartEditSkeleton
                        data={components.DLGSubscriptionPaymentMethodBox}
                      />
                    </div>
                  )
                : components.DLGPaymentMethodBox && (
                    <div className='dln-cart__payment-wrapper'>
                      <SmartEditSkeleton
                        data={components.DLGPaymentMethodBox}
                      />
                    </div>
                  )}
              <div className='dln-cart__button-wrapper'>
                <Button
                  onClick={goToCheckoutHandler}
                  text={t('cart:cart.page.checkout.label')}
                  modifiers={['checkout']}
                />
                {components.DLGAmazonPayButton &&
                  amazonPayEnabled === 'true' && (
                    <SmartEditSkeleton data={components.DLGAmazonPayButton} />
                  )}
              </div>
            </div>
          )}
          {!isDesktop() && (
            <div className='js-sfmc-personalization-mobile-cart' />
          )}
          <div className='dln-cart__noticeBox'>
            <SmartEditSkeleton data={components.DLGBenefitLight} />
          </div>
          {hasSubscriptions
            ? !hasEntries &&
              components.DLGSubscriptionPaymentMethodBox && (
                <SmartEditSkeleton
                  data={components.DLGSubscriptionPaymentMethodBox}
                />
              )
            : !hasEntries &&
              components.DLGPaymentMethodBox && (
                <SmartEditSkeleton data={components.DLGPaymentMethodBox} />
              )}
        </div>
      </div>
    </div>
  );
};

const CartPage = () => (
  <CmsProvider>
    <CartProvider>
      <CartApp />
    </CartProvider>
  </CmsProvider>
);
export default CartPage;
